import { useState } from 'react';

import { useIndirectTaxFilingV2 } from '@commenda/api/indirect-tax/filings';
import { updateTask } from '@commenda/api/service-request';
import {
  pairHasOpenIssue,
  useTaskActivitiesForServiceRequest,
} from '@commenda/api/tasks';
import { Button, useToggle } from '@commenda/kakashi';
import { SalesTaxFilingTasks } from '@commenda/types/src/sales-tax/types';
import { TaskParty, TaskStatus } from '@prisma/client';

import {
  IndirectTaxRegion,
  IndirectTaxRegionHelper,
} from '../../../indirect-tax/common';
import { IndirectTaxFilingsHelper } from '../../../indirect-tax/filings/helpers';
import { ServiceRequestTaskLabel } from '../../common/labels';
import { ServiceRequestTaskIconConfig } from '../../common/taskIconConfig';
import MarkAsCompleteTask from '../../common/tasks/MarkAsCompleteTask';
import { ServiceRequestTaskCard } from '../../layout/components/taskCard/ServiceRequestTaskCard';
import { ServiceRequestTaskCardConfig, TCommonTaskProp } from '../../types';
import { IndirectTaxFilingVatReportModal } from './components/IndirectTaxFilingVatReportModal';

export const IndirectTaxFilingGenerateReportTask = (props: TCommonTaskProp) => {
  const { task, context, isTimelineView, displayNumber } = props;
  const { role, serviceRequest, mutate: refetchServiceRequest } = context;

  const filingId =
    serviceRequest?.salesTaxFiling?.filing_id ??
    serviceRequest?.salesTaxFilingId;

  const { filing, mutate: refetchFiling } = useIndirectTaxFilingV2(
    filingId,
    filingId ? { corporationId: serviceRequest.corporationId } : null
  );

  const [isCompleting, setIsCompleting] = useState(false);
  const reportModal = useToggle();

  const region: IndirectTaxRegion = filing
    ? IndirectTaxRegionHelper.deriveRegionFromJurisdiction({
        jurisdictionType: filing.jurisdictionType,
        jurisdictionName: filing.jurisdictionName,
        country: filing.country,
      })
    : IndirectTaxRegion.UNITED_STATES;
  const isContributionDriven =
    IndirectTaxRegionHelper.isContributionRegion(region);
  const currency = filing
    ? IndirectTaxFilingsHelper.getFilingCurrency(filing, region)
    : '';
  const { corporationId } = serviceRequest;

  const { data: activityFeed } = useTaskActivitiesForServiceRequest(
    serviceRequest?.id
  );

  const reviewTask = serviceRequest?.taskGroups
    ?.flatMap((group) => group.tasks)
    .find(
      (t) =>
        t.legacyServiceRequestTaskTaskId ===
        SalesTaxFilingTasks.REVIEW_TRANSACTIONS
    );
  const clientReportedIssue = pairHasOpenIssue(
    activityFeed?.activities,
    task,
    reviewTask
  );

  const markCompleteHandler = async () => {
    setIsCompleting(true);
    await updateTask({
      id: task.id,
      data: { status: TaskStatus.COMPLETED },
    });
    if (refetchServiceRequest) refetchServiceRequest();
    setIsCompleting(false);
  };

  const handleReportSubmitted = () => {
    refetchFiling();
    if (refetchServiceRequest) refetchServiceRequest();
  };

  if (!filingId) {
    return <MarkAsCompleteTask {...props} />;
  }

  const reviewButton = (
    <Button variant="secondary" size="sm" onClick={reportModal.show}>
      Review &amp; submit filing
    </Button>
  );

  const markReviewedButton = (
    <Button
      variant="secondary"
      size="sm"
      onClick={markCompleteHandler}
      disabled={isCompleting}
    >
      Mark reviewed
    </Button>
  );

  const clientStateMap = {
    [TaskStatus.TO_DO]: {
      actions: null,
      label: (
        <ServiceRequestTaskLabel label="Commenda team is reviewing your filing" />
      ),
    },
    [TaskStatus.IN_PROGRESS]: {
      actions: null,
      label: (
        <ServiceRequestTaskLabel
          label="Commenda team is reviewing your filing"
          status={task.status}
        />
      ),
    },
    [TaskStatus.COMPLETED]: {
      actions: null,
      label: (
        <ServiceRequestTaskLabel label="Filing reviewed" status={task.status} />
      ),
    },
    [TaskStatus.BACKLOG]: {
      actions: null,
      label: (
        <ServiceRequestTaskLabel label="Commenda team is reviewing your filing" />
      ),
    },
  };

  // Contribution-driven filings: the agent opens the filing-report form (Save/Submit). The form
  // also reopens here (back to IN_PROGRESS) when the client reports an issue,
  // in which case the IN_PROGRESS arm swaps to the revise-and-resubmit copy.
  const reportAgentStateMap = {
    [TaskStatus.TO_DO]: {
      actions: reviewButton,
      label: (
        <ServiceRequestTaskLabel label="Review the filing report and submit for client approval" />
      ),
    },
    [TaskStatus.IN_PROGRESS]: {
      actions: reviewButton,
      label: (
        <ServiceRequestTaskLabel
          label={
            clientReportedIssue
              ? 'Client reported an issue — revise the filing report and resubmit'
              : 'Review the filing report and submit for client approval'
          }
          status={task.status}
        />
      ),
    },
    [TaskStatus.COMPLETED]: {
      actions: null,
      label: (
        <ServiceRequestTaskLabel label="Filing reviewed" status={task.status} />
      ),
    },
    [TaskStatus.BACKLOG]: {
      actions: null,
      label: (
        <ServiceRequestTaskLabel label="Review the filing report and submit for client approval" />
      ),
    },
  };

  // Flat-field filings (US): simple "Mark reviewed" agent action.
  const defaultAgentStateMap = {
    [TaskStatus.TO_DO]: {
      actions: markReviewedButton,
      label: (
        <ServiceRequestTaskLabel label="Review filing data and hand off to client for approval" />
      ),
    },
    [TaskStatus.IN_PROGRESS]: {
      actions: markReviewedButton,
      label: (
        <ServiceRequestTaskLabel
          label="Review filing data and hand off to client for approval"
          status={task.status}
        />
      ),
    },
    [TaskStatus.COMPLETED]: {
      actions: null,
      label: (
        <ServiceRequestTaskLabel label="Filing reviewed" status={task.status} />
      ),
    },
    [TaskStatus.BACKLOG]: {
      actions: null,
      label: (
        <ServiceRequestTaskLabel label="Review filing data and hand off to client for approval" />
      ),
    },
  };

  const config: ServiceRequestTaskCardConfig = {
    getLeftIcon: (status) => ServiceRequestTaskIconConfig[status],
    getStateConfig: (state) => {
      const stateMap = {
        [TaskParty.CLIENT]: clientStateMap,
        [TaskParty.AGENT]: isContributionDriven
          ? reportAgentStateMap
          : defaultAgentStateMap,
      };

      return (
        stateMap[state.role]?.[state.status] ?? {
          label: task.title,
          actions: null,
        }
      );
    },
  };

  return (
    <>
      <ServiceRequestTaskCard
        task={task}
        state={{ role, status: task.status }}
        config={config}
        context={context}
        isTimelineView={isTimelineView}
        displayNumber={displayNumber}
      />

      {isContributionDriven && serviceRequest?.id ? (
        <IndirectTaxFilingVatReportModal
          isOpen={reportModal.visible}
          onClose={reportModal.off}
          filingId={filingId}
          corporationId={corporationId}
          taskId={task.id}
          serviceRequestId={serviceRequest.id}
          currency={currency}
          onSubmitted={handleReportSubmitted}
        />
      ) : null}
    </>
  );
};
